import React, { forwardRef, useMemo } from 'react';

import { useNavigation } from '@react-navigation/native';

import { PromoWidget, PromoWidgetItem, PromoWidgetRef } from '@/components/promo-widget/PromoWidget';
import { BetrAnalytics } from '@/feature/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { useBetslipActions } from '@/feature/betslip-pickem/hooks/use-betslip-actions';
import { useBetslipData } from '@/feature/betslip-pickem/hooks/use-betslip-data';
import { useBetslipStore } from '@/feature/betslip-pickem/hooks/use-betslip-store';
import {
    FantasyEntryPromotionStatus,
    useFantasyEntryPromotions,
} from '@/feature/betslip-pickem/hooks/use-fantasy-entry-promotions';
import { getFreeEntryBonusAmount, getPromotionTypeLabel } from '@/feature/betslip-pickem/utils/betslip-utils';
import { CriterionType } from '@/types/api.generated';

/**
 * Maps fantasy entry promotions onto the shared PromoWidget and re-validates picks on selection.
 */
export const PickemPromoWidget = forwardRef<PromoWidgetRef>((_, ref) => {
    const selectedPromo = useBetslipStore(state => state.selectedPromo);
    const setSelectedPromo = useBetslipStore(state => state.actions.setSelectedPromo);
    const setEntryAmount = useBetslipStore(state => state.actions.setEntryAmount);
    const entryAmount = useBetslipStore(state => state.entryAmount ?? 0);
    const currency = useBetslipStore(state => state.currency);
    const { entryPromotions } = useFantasyEntryPromotions();
    const navigation = useNavigation();

    const { validatePicks, removeSelection } = useBetslipActions();
    const { betslip } = useBetslipData();

    const promosWithStatus: PromoWidgetItem[] = useMemo(() => {
        return entryPromotions.map(promo => ({
            id: promo.promotion.id,
            name: promo.promotion.name,
            label: getPromotionTypeLabel(promo),
            description: promo.promotion.description,
            status: selectedPromo?.promotion.id === promo.promotion.id ? 'applied' : 'available',
            error: getPromotionError(promo),
            onInfoPress: promo.promotion.type === 'MYSTERY' ? () => navigation.navigate('MysteryInfoModal') : undefined,
        }));
    }, [entryPromotions, selectedPromo, navigation]);

    const handleSelectPromo = (promoId: string) => {
        const promotionIsAlreadySelected = selectedPromo?.promotion.id === promoId;
        let validationAmount = entryAmount;
        if (promotionIsAlreadySelected) {
            BetrAnalytics.trackEvent(AnalyticsEvent.REMOVE_PROMO_FROM_CHECKOUT, { promoId });
            setSelectedPromo(undefined);
        } else {
            BetrAnalytics.trackEvent(AnalyticsEvent.APPLY_PROMO_FROM_CHECKOUT, { promoId });
            const promoToApply = entryPromotions.find(promo => promo.promotion.id === promoId);
            setSelectedPromo(promoToApply);

            // Free entry promos force the entry amount to the promo's bonus amount
            const freeEntryBonusAmount = getFreeEntryBonusAmount(promoToApply);
            if (freeEntryBonusAmount !== undefined) {
                setEntryAmount(freeEntryBonusAmount);
                validationAmount = freeEntryBonusAmount;
            }
        }

        // Validate picks after promotion selection/deselection
        validatePicks(betslip, validationAmount, currency, undefined, removeSelection);
    };

    return (
        <PromoWidget
            ref={ref}
            promos={promosWithStatus}
            onSelectPromo={handleSelectPromo}
            testID="available-promos-widget"
        />
    );
});

const getPromotionError = (promo: FantasyEntryPromotionStatus): PromoWidgetItem['error'] => {
    if (!promo.errors?.length) {
        return undefined;
    }
    const cashOnlyError = promo.errors.find(error => error.type === CriterionType.CashOnly);
    return { shortDescription: cashOnlyError?.shortDescription ?? promo.errors[0]?.shortDescription ?? '' };
};
